import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectReportingV2AnyLoading,
  selectReportingV2Filters,
  setReportingV2Filters,
} from '@/store/slices/reportingV2/reportingV2Slice';
import { Box, LinearProgress } from '@mui/material';
import { useState } from 'react';
import { channelLabel, deviceLabel } from '../format';
import { useReportingPermissions } from '../hooks/useReportingPermissions';
import { COLORS, cardSx } from '../theme';
import BreakdownCard from './BreakdownCard';
import ClientRecapCard from './ClientRecapCard';
import Conversions from './Conversions';
import ConversionsBreakdown from './ConversionsBreakdown';
import DayOfWeekCard from './DayOfWeekCard';
import DaypartCard from './DaypartCard';
import Dv360ConversionsCard from './Dv360ConversionsCard';
import Dv360CreativeLineItemPerformanceCard from './Dv360CreativeLineItemPerformanceCard';
import GeographicPerformance from './GeographicPerformance';
import GoogleAdsCampaignBreakdownCard from './GoogleAdsCampaignBreakdownCard';
import GoogleAdsConversionActionsCard from './GoogleAdsConversionActionsCard';
import GoogleAdsDeviceConversionsCard from './GoogleAdsDeviceConversionsCard';
import GroupedBreakdownCard from './GroupedBreakdownCard';
import ImpressionsByType from './ImpressionsByType';
import ImpressionsOverTime from './ImpressionsOverTime';
import KpiCompareTable from './KpiCompareTable';
import KpiStrip from './KpiStrip';
import OutcomesStrip from './OutcomesStrip';
import ReportingV2FilterBar from './ReportingV2FilterBar';
import ReportingV2Header from './ReportingV2Header';
import TopCampaignsCard from './TopCampaignsCard';
import TopPublishersCard from './TopPublishersCard';
import WebConversionsFunnel from './WebConversionsFunnel';

/**
 * Kill switch for the DV360 Floodlight conversions card (flank#4705). Its
 * backend permission gate (ribeye-api#2495) isn't deployed yet — leaving this
 * on would 500 in prod the moment this release ships. Flip back to `true`
 * once #2495 is live.
 */
const DV360_FLOODLIGHT_LIVE = false;

/**
 * Kill switch for the Google Ads Campaign Breakdown card (flank#4006). Its two
 * new metrics (ribeye-api#2595) are deployed to preprod as of 2026-10-28 —
 * flipped on to verify there. Flip back to `false` if ribeye-api's `main` →
 * `prod` promotion ships before #2595 reaches `prod`, or this 500s in
 * production the moment this flank release ships there.
 */
const GOOGLE_ADS_CAMPAIGN_BREAKDOWN_LIVE = true;

/**
 * Kill switch for the Google Ads Conversion Actions card (flank#4009). Its
 * `conversion_action` dimension (ribeye-api#2605) is live in prod as of
 * 2026-10-29. Flip back to `false` if this card ever 422s.
 */
const GOOGLE_ADS_CONVERSION_ACTIONS_LIVE = true;

const twoCol = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
  gap: 3,
  width: '100%',
  minWidth: 0,
} as const;

/**
 * Title, actions and filters used to be two separate blocks taking 40-50% of
 * the viewport before any data appeared. #4626 combines them into one card
 * pinned to the top of the scroll area (like a spreadsheet's frozen row), so
 * the first widget is visible without scrolling. `position: sticky` resolves
 * against the window (flank#4652 review: AppLayout's shell around `<main>`
 * isn't a real scroll container — it has no fixed height, so its content
 * never overflows it and the window scrolls instead; it must stay free of
 * `overflow-y-auto`/`hidden` or sticky gets a scroll frame that never fires).
 * Reset to static for print: print has no scroll position, and leaving it
 * sticky risks the browser re-painting it on every page instead of once.
 */
const stickyHeaderSx = {
  ...cardSx,
  position: 'sticky',
  top: 0,
  // Above MapHoverTooltip's 10 (review flank#4652) — the map can scroll
  // under the sticky header, and its tooltip must not paint over it.
  zIndex: 11,
  display: 'flex',
  flexDirection: 'column',
  gap: 1.75,
  p: 2.5,
  '@media print': { ...cardSx['@media print'], position: 'static', boxShadow: 'none' },
} as const;

const ReportingV2ProgressBar = () => {
  const loading = useAppSelector(selectReportingV2AnyLoading);
  return <Box sx={{ height: 4 }}>{loading && <LinearProgress sx={{ borderRadius: 2 }} />}</Box>;
};

const toggle = (arr: string[], value: string) => (arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value]);

const ReportingV2View = () => {
  const dispatch = useAppDispatch();
  const filters = useAppSelector(selectReportingV2Filters);
  const [showCompare, setShowCompare] = useState(false);
  // Impressions by Channel's default labelFn (dimensionLabel) would otherwise
  // print GAM's own channel values verbatim — "GAM Display"/"GAM Video" bake
  // the platform name into the channel itself, unlike every other DSP's same
  // channel type (flank#4741 QA: a second DSP-identifying leak alongside the
  // Platform/DSP chip, for a user without canViewPlatform).
  const { canViewPlatform } = useReportingPermissions();

  const handleChannelSelect = (value: string) =>
    dispatch(setReportingV2Filters({ channelIds: toggle(filters.channelIds, value) }));

  const handleDeviceSelect = (value: string) =>
    dispatch(setReportingV2Filters({ deviceIds: toggle(filters.deviceIds, value) }));

  return (
    <Box
      // Hook for the scoped print rule in index.css — see reporting-v2-root.
      className='reporting-v2-root'
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        width: '100%',
        minWidth: 0,
        // `clip`, not `hidden`: setting only overflow-x to a non-visible value
        // forces the browser to resolve overflow-y away from `visible` too —
        // to `auto` for `hidden`, which would make this Box its own scroll
        // container and break the sticky header below (it would stick to
        // THIS box instead of the window, contradicting the comment above).
        // `clip` is exempted from that coercion (CodeRabbit, #4652).
        overflowX: 'clip',
        p: 3,
        bgcolor: COLORS.pageBg,
        minHeight: '100%',
      }}
    >
      {/* One sticky, pinned header (#4626): title/actions and filters used to
          be two separate blocks direct children of this flex column — kept
          that way (not re-wrapped further) so the print rules below, which
          target `.reporting-v2-root > *`, still see every widget as a direct
          child and keep their per-card break-inside/spacing behavior. */}
      <Box sx={stickyHeaderSx}>
        <ReportingV2Header />
        <ReportingV2FilterBar />
        <ReportingV2ProgressBar />
      </Box>

      <Box data-export-section='clientRecap'>
        <ClientRecapCard />
      </Box>

      <Box data-export-section='grouped'>
        <GroupedBreakdownCard />
      </Box>

      <Box data-export-section='totals'>
        <KpiStrip onCompareToggle={() => setShowCompare(v => !v)} compareActive={showCompare} />
      </Box>
      {showCompare && (
        <Box data-export-section='compareTable'>
          <KpiCompareTable />
        </Box>
      )}

      <Box data-export-section='trend'>
        <ImpressionsOverTime />
      </Box>

      <Box className='rv2-grid' sx={twoCol}>
        <Box data-export-section='channel'>
          <BreakdownCard
            widgetKey='channel'
            title='Impressions by Channel'
            labelKey='channel'
            valueKey='impressions'
            labelFn={value => channelLabel(value, canViewPlatform)}
            selectedValues={filters.channelIds}
            onSelect={handleChannelSelect}
            // Deliberately names no platform: this said "only Beeswax carries channel"
            // and went stale the day DV360 channel mapping landed, contradicting the
            // chart directly above it. The share is computed, so the sentence doesn't
            // need to be — and won't need editing as more platforms get mapped.
            //
            // Also deliberately names no single mechanism (flank#4868): GAM, DV360,
            // Google Ads, Bing Ads, AdsWizz and Social infer channel from feed
            // signals rather than (or in addition to) the line item, so "mapped per
            // line item" described only one of several resolution paths.
            unmappedNote="Channel comes from the line item for some platforms and from feed signals for others, so platforms that haven't resolved a channel yet sit outside this breakdown. The delivery is real, it just has no channel attached."
          />
        </Box>
        <Box data-export-section='device'>
          <BreakdownCard
            widgetKey='device'
            title='Impressions by Device'
            labelKey='device'
            valueKey='impressions'
            labelFn={deviceLabel}
            selectedValues={filters.deviceIds}
            onSelect={handleDeviceSelect}
            unmappedNote="Device isn't populated for these platforms yet. This isn't a display error; the delivery is real, it just has no device attached."
          />
        </Box>
      </Box>

      {/* Below the trend chart and the breakdown widgets: the funnel is a
          single-platform figure, so it reads as a footnote to the delivery
          above it rather than a headline. It renders only when the filters
          resolve to one platform with conversions. */}
      <Box data-export-section='outcomesStrip'>
        <OutcomesStrip />
      </Box>

      <Box data-export-section='geoPerf'>
        <GeographicPerformance />
      </Box>

      <Box data-export-section='impressionsByType'>
        <ImpressionsByType />
      </Box>

      {/* Attribution's own widget (flank#4648), directly below Impressions by Type
          where its predecessor (#4451's ConversionsByType card) lived. */}
      <Box data-export-section='conversions'>
        <Conversions />
      </Box>

      {/* Web conversions by event (flank#4647). The By channel/By platform
          "Conversions by Type" card that used to render here was removed per
          product decision — cross-platform conversion totals overlap, so
          they shouldn't be summed (prep #0000). */}
      <Box data-export-section='conversionsBreakdown'>
        <ConversionsBreakdown />
      </Box>

      {/* Hidden pending ribeye-api#2495 (review, Lucas): #4705's backend
          permission gate for this card isn't on API main yet, so it would
          error in prod if flank shipped first. Remove this guard once #2495
          merges and deploys — the card itself needs no other changes. */}
      {DV360_FLOODLIGHT_LIVE && (
        <Box data-export-section='dv360Conversions'>
          <Dv360ConversionsCard />
        </Box>
      )}

      <Box className='rv2-grid' sx={twoCol}>
        <Box data-export-section='dayOfWeek'>
          <DayOfWeekCard />
        </Box>
        <Box data-export-section='daypartMetric'>
          <DaypartCard />
        </Box>
      </Box>

      <Box data-export-section='topCampaignsMetric'>
        <TopCampaignsCard />
      </Box>
      {GOOGLE_ADS_CAMPAIGN_BREAKDOWN_LIVE && (
        <Box data-export-section='googleAdsCampaignBreakdown'>
          <GoogleAdsCampaignBreakdownCard />
        </Box>
      )}
      <Box className='rv2-grid' sx={twoCol}>
        <Box data-export-section='googleAdsDeviceConversions'>
          <GoogleAdsDeviceConversionsCard />
        </Box>
        {GOOGLE_ADS_CONVERSION_ACTIONS_LIVE && (
          <Box data-export-section='googleAdsConversionActions'>
            <GoogleAdsConversionActionsCard />
          </Box>
        )}
      </Box>
      <Box data-export-section='dv360CreativeLineItemPerformance'>
        <Dv360CreativeLineItemPerformanceCard />
      </Box>
      <Box data-export-section='topPublishersMetric'>
        <TopPublishersCard />
      </Box>

      <WebConversionsFunnel />
    </Box>
  );
};

export default ReportingV2View;
